import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type { ApiTokenScope, ApiTokenSummary } from '@bits/shared/zod';

export const settingsApiTokensRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/api-tokens',
  component: ApiTokensPage,
});

const AVAILABLE_SCOPES: Array<{ value: ApiTokenScope; label: string; description: string }> = [
  { value: 'read', label: 'read', description: 'Query anything you can see in the web UI' },
  { value: 'write:sales', label: 'write:sales', description: 'Create prospects, log activities, move stages' },
  { value: 'write:crm', label: 'write:crm', description: 'Add client contacts' },
  { value: 'write:ideas', label: 'write:ideas', description: 'Comment on ideas' },
  { value: 'write:notifications', label: 'write:notifications', description: 'Mark notifications read' },
];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function ApiTokensPage(): JSX.Element {
  const queryClient = useQueryClient();
  const tokensQuery = useQuery({
    queryKey: ['api-tokens'],
    queryFn: () => api.listApiTokens(),
  });

  const [name, setName] = React.useState('');
  const [selectedScopes, setSelectedScopes] = React.useState<Set<ApiTokenScope>>(new Set(['read']));
  const [expiresInDays, setExpiresInDays] = React.useState<string>('');
  const [error, setError] = React.useState<string | null>(null);
  const [freshToken, setFreshToken] = React.useState<{ id: string; token: string } | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.createApiToken({
        name: name.trim(),
        scopes: [...selectedScopes],
        expiresInDays: expiresInDays ? parseInt(expiresInDays, 10) : undefined,
      }),
    onSuccess: async (result) => {
      setFreshToken(result);
      setName('');
      setSelectedScopes(new Set(['read']));
      setExpiresInDays('');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeApiToken(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
    },
  });

  const toggleScope = (scope: ApiTokenScope): void => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  };

  const canSubmit = name.trim().length > 0 && selectedScopes.size > 0 && !create.isPending;
  const tokens: ApiTokenSummary[] = tokensQuery.data ?? [];
  const active = tokens.filter((t) => t.revokedAt === null);
  const revoked = tokens.filter((t) => t.revokedAt !== null);

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
          API tokens
        </h1>
        <p className="text-base text-[hsl(var(--muted-foreground))]">
          Personal access tokens for MCP and integrations. Each token authenticates as you and inherits your role.
          Shown once at creation — copy and store securely.
        </p>
      </div>

      {freshToken ? (
        <Alert>
          <AlertTitle>Token created — copy it now</AlertTitle>
          <AlertDescription>
            <div className="mt-2 flex items-center gap-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-3">
              <code className="flex-1 break-all font-mono text-sm">{freshToken.token}</code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(freshToken.token);
                }}
              >
                Copy
              </Button>
            </div>
            <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
              You will not be able to view this value again. If you lose it, revoke and create a new one.
            </p>
            <div className="mt-3">
              <Button variant="ghost" size="sm" onClick={() => setFreshToken(null)}>
                Dismiss
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Create a new token</CardTitle>
          <CardDescription>Name it after where it will run (e.g. "Claude Desktop — Kamal").</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="token-name">Name</Label>
              <Input
                id="token-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Claude Desktop — Kamal"
                maxLength={120}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="token-expires">Expires in (days, optional)</Label>
              <Input
                id="token-expires"
                type="number"
                min="1"
                max="365"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                placeholder="Never expires"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Scopes</Label>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {AVAILABLE_SCOPES.map((s) => (
                <label
                  key={s.value}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-[hsl(var(--border))] p-3 hover:bg-[hsl(var(--accent))]"
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selectedScopes.has(s.value)}
                    onChange={() => toggleScope(s.value)}
                  />
                  <span className="flex flex-col gap-0.5">
                    <code className="text-sm font-medium">{s.label}</code>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{s.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {error ? (
            <Alert>
              <AlertTitle>Failed to create token</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div>
            <Button onClick={() => create.mutate()} disabled={!canSubmit}>
              {create.isPending ? 'Creating…' : 'Create token'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Active tokens</CardTitle>
          <CardDescription>{active.length === 0 ? 'None yet.' : `${active.length} active`}</CardDescription>
        </CardHeader>
        <CardContent>
          {active.length === 0 ? null : (
            <ul className="flex flex-col gap-2">
              {active.map((t) => (
                <li
                  key={t.id}
                  className="flex flex-col gap-2 rounded-lg border border-[hsl(var(--border))] p-4 md:flex-row md:items-center md:justify-between"
                >
                  <div className="flex flex-col gap-1">
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="flex flex-wrap gap-1">
                      {t.scopes.map((s) => (
                        <Badge key={s} variant="muted">
                          <code className="text-xs">{s}</code>
                        </Badge>
                      ))}
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      Created {formatDate(t.createdAt)} · Last used {formatDate(t.lastUsedAt)}
                      {t.expiresAt ? ` · Expires ${formatDate(t.expiresAt)}` : ''}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => revoke.mutate(t.id)}
                    disabled={revoke.isPending}
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {revoked.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Revoked</CardTitle>
            <CardDescription>{revoked.length} revoked · kept for audit trail</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col gap-2">
              {revoked.map((t) => (
                <li
                  key={t.id}
                  className="flex flex-col gap-1 rounded-lg border border-[hsl(var(--border))] p-3 text-sm text-[hsl(var(--muted-foreground))]"
                >
                  <div className="font-medium text-[hsl(var(--foreground))]/70">{t.name}</div>
                  <div className="text-xs">
                    Created {formatDate(t.createdAt)} · Revoked {formatDate(t.revokedAt)}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </AppShell>
  );
}
